import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";
import { Target, RotateCcw, TrendingUp } from "lucide-react";
import { ease } from "./utils";

const signals = [
  { tag: "SCALE", sub: "Customer Reactivation", icon: TrendingUp, z: 46, ml: 26, reveal: 0.85, drift: 4, dur: 6.5, fdelay: 1.6 },
  { tag: "RECOVER", sub: "Quote Follow-Up", icon: RotateCcw, z: 30, ml: -12, reveal: 0.7, drift: 3, dur: 7, fdelay: 0.8 },
  { tag: "CAPTURE", sub: "Lead Response", icon: Target, z: 16, ml: 20, reveal: 0.55, drift: 3.5, dur: 6, fdelay: 0.2 },
];

function SignalCard({ icon: Icon, tag, sub, z, ml, reveal, drift, dur, fdelay, ready, reduce }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, z }}
      animate={ready ? { opacity: 1, scale: 1, z, y: reduce ? 0 : [0, -drift, 0] } : { opacity: 0, scale: 0.95, z }}
      transition={{
        opacity: { duration: 0.6, ease, delay: ready ? reveal : 0 },
        scale: { duration: 0.6, ease, delay: ready ? reveal : 0 },
        y: { duration: dur, repeat: Infinity, ease: "easeInOut", delay: fdelay },
      }}
      style={{ marginLeft: ml, transformStyle: "preserve-3d" }}
      className="relative w-[78%] max-w-[300px]"
    >
      <motion.div
        whileHover={reduce ? {} : { y: -5 }}
        className="group relative flex items-center gap-4 rounded-2xl glass-panel px-5 py-4 shadow-luxe transition-colors duration-300 hover:border-brand-gold/55 hover:shadow-luxe-gold"
      >
        <span className="absolute left-1/2 top-full hidden h-4 w-px -translate-x-1/2 bg-brand-gold/40 last:hidden sm:block" />
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-gold/12 transition-all duration-300 group-hover:bg-brand-gold/25">
          <Icon className="h-5 w-5 text-brand-gold transition-transform duration-300 group-hover:-translate-y-0.5" strokeWidth={1.6} />
        </span>
        <div className="min-w-0">
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-gold-deep">{tag}</p>
          <p className="mt-0.5 font-sans text-[15px] font-semibold tracking-tight text-brand-black">{sub}</p>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Plaque({ ready, reduce }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.92, z: 70 }}
      animate={ready ? { opacity: 1, scale: 1, z: 70, y: reduce ? 0 : [0, -6, 0] } : { opacity: 0, scale: 0.92, z: 70 }}
      transition={{
        opacity: { duration: 0.7, ease, delay: ready ? 0.3 : 0 },
        scale: { duration: 0.7, ease, delay: ready ? 0.3 : 0 },
        y: { duration: 6, repeat: Infinity, ease: "easeInOut" },
      }}
      style={{ transformStyle: "preserve-3d" }}
      className="relative mx-auto w-[58%] max-w-[230px]"
    >
      {/* breathing gold glow */}
      <motion.div
        className="pointer-events-none absolute -inset-6 rounded-[2rem] bg-brand-gold/25 blur-3xl"
        animate={reduce ? { opacity: 0.45 } : { opacity: [0.3, 0.6, 0.3], scale: [0.95, 1.06, 0.95] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* slow gold orbit line */}
      {!reduce && (
        <svg className="pointer-events-none absolute -inset-7 h-[calc(100%+3.5rem)] w-[calc(100%+3.5rem)]" viewBox="0 0 200 200" fill="none">
          <ellipse cx="100" cy="100" rx="92" ry="60" stroke="#C89A3D" strokeOpacity="0.18" strokeWidth="1" transform="rotate(-18 100 100)" />
          <motion.ellipse
            cx="100" cy="100" rx="92" ry="60"
            stroke="#C89A3D" strokeWidth="1.5" strokeLinecap="round"
            transform="rotate(-18 100 100)"
            strokeDasharray="40 420"
            animate={{ strokeDashoffset: [0, -460] }}
            transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
          />
        </svg>
      )}

      {/* beveled gold-edge plaque */}
      <div className="relative rounded-[1.4rem] bg-gradient-to-br from-brand-gold/70 via-brand-gold/25 to-brand-gold-deep/60 p-[1.5px] shadow-[0_30px_60px_-20px_rgba(17,17,17,0.45)]">
        <div className="relative overflow-hidden rounded-[1.3rem] bg-white p-6">
          {/* glass reflection */}
          <div className="pointer-events-none absolute -left-1/4 -top-1/2 h-[180%] w-1/2 rotate-[18deg] bg-gradient-to-b from-white/70 via-white/10 to-transparent opacity-60" />
          {/* entrance light sweep */}
          {!reduce && (
            <motion.div
              className="pointer-events-none absolute inset-0 overflow-hidden"
              initial={{ opacity: 0 }}
              animate={ready ? { opacity: [0, 1, 0] } : { opacity: 0 }}
              transition={{ duration: 1.1, delay: ready ? 0.6 : 0, ease }}
            >
              <motion.div
                className="absolute top-0 h-full w-1/3 -skew-x-12"
                style={{ background: "linear-gradient(90deg, transparent, rgba(200,154,61,0.4), rgba(255,255,255,0.5), transparent)" }}
                initial={{ left: "-40%" }}
                animate={ready ? { left: "130%" } : { left: "-40%" }}
                transition={{ duration: 1, delay: ready ? 0.6 : 0, ease }}
              />
            </motion.div>
          )}
          <img src="/apex-flow-logo.png" alt="Apex Flow AI" draggable={false} className="relative z-10 mx-auto h-auto w-full object-contain" />
        </div>
      </div>
    </motion.div>
  );
}

export default function GrowthSignal({ ready = true }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const rotX = useSpring(useTransform(py, [-0.5, 0.5], [6, -6]), { stiffness: 80, damping: 18 });
  const rotY = useSpring(useTransform(px, [-0.5, 0.5], [-9, 9]), { stiffness: 80, damping: 18 });

  const onMove = (e) => {
    if (reduce || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    px.set((e.clientX - r.left) / r.width - 0.5);
    py.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    px.set(0);
    py.set(0);
  };

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} className="relative mx-auto w-full max-w-[460px] [perspective:1300px]">
      <motion.div style={{ rotateX: rotX, rotateY: rotY, transformStyle: "preserve-3d" }} className="relative">
        <motion.div
          initial={{ opacity: 0 }}
          animate={ready ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.8, ease }}
          className="relative flex flex-col items-center preserve-3d"
        >
          <Plaque ready={ready} reduce={reduce} />

          {/* pathway + cards */}
          <div className="relative mt-5 w-full">
            {/* gold pathway, draws upward */}
            <motion.span
              className="absolute left-1/2 top-2 bottom-3 w-px -translate-x-1/2 origin-bottom bg-gradient-to-t from-brand-gold/15 via-brand-gold/55 to-brand-gold/80"
              initial={{ scaleY: 0 }}
              animate={ready ? { scaleY: 1 } : { scaleY: 0 }}
              transition={{ duration: 1.1, ease, delay: ready ? 0.4 : 0 }}
            />
            {!reduce && (
              <motion.span
                className="absolute left-1/2 h-2 w-2 -translate-x-1/2 rounded-full bg-brand-gold shadow-[0_0_10px_rgba(200,154,61,0.9)]"
                initial={{ top: "92%", opacity: 0 }}
                animate={ready ? { top: ["92%", "4%"], opacity: [0, 1, 1, 0] } : {}}
                transition={{ duration: 4.5, delay: 1.6, repeat: Infinity, ease: "easeInOut" }}
              />
            )}

            <div className="relative flex flex-col items-center gap-4 sm:gap-5">
              {signals.map((s) => (
                <SignalCard key={s.tag} {...s} ready={ready} reduce={reduce} />
              ))}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
